define('calendarEditView',[
  'app',
  'module',
  'underscore',
  'jquery',
  'moment',
  'modalsConfirmationView',
  'dateUtils',
  'bootbox',
  'template!calendarEditView'
], function (
  App,
  Module,
  _,
  $,
  moment,
  ModalsConfirmationView,
  dateUtils,
  bootbox,
  Tpl
) {
  'use strict';

  var WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

  // Default colour per category — keeps the swatch in sync with the legend so
  // the category colour-coding actually drives event colour.
  var CATEGORY_COLORS = {
    MEETING: '#2f8f91',
    VACATION: '#2e9e4f',
    DEADLINE: '#e0564c',
    OTHER: '#6b7280'
  };

  Module.exports = ModalsConfirmationView.extend({
    template: Tpl,
    className: 'calendar-edit-view ' + ModalsConfirmationView.prototype.className,

    ui: _.extend({
      title: '.js-title',
      description: '.js-description',
      type: '.js-type',
      groupRow: '.js-group-row',
      group: '.js-group',
      category: '.js-category',
      color: '.js-color',
      allDay: '.js-all-day',
      start: '.js-start',
      end: '.js-end',
      halfDayRow: '.js-half-day-row',
      halfDayPart: '.js-half-day-part',
      recurrenceFreq: '.js-recurrence-freq',
      recurrenceOptions: '.js-recurrence-options',
      intervalRow: '.js-interval-row',
      byDayRow: '.js-byday-row',
      byDay: '.js-byday',
      recurrenceInterval: '.js-recurrence-interval',
      recurrenceUntil: '.js-recurrence-until',
      reminder: '.js-reminder',
      deleteBtn: '.js-delete',
      approveBtn: '.js-approve',
      rejectBtn: '.js-reject'
    }, ModalsConfirmationView.prototype.ui),

    events: {
      'change @ui.type': 'onTypeChange',
      'change @ui.category': 'onCategoryChange',
      'change @ui.allDay': 'onAllDayChange',
      'change @ui.recurrenceFreq': 'onRecurrenceChange',
      'click @ui.deleteBtn': 'onDelete',
      'click @ui.approveBtn': 'onApprove',
      'click @ui.rejectBtn': 'onReject'
    },

    initialize: function () {
      this.cloneModel = this.model.clone();
      ModalsConfirmationView.prototype.initialize.apply(this, arguments);
    },

    regions: _.extend({}, ModalsConfirmationView.prototype.regions),

    serializeData: function () {
      var m = this.model;
      var type = m.get('type') || 'PERSONAL';
      var status = m.get('status') || 'CONFIRMED';
      var data = {
        isNew: !m.get('secId'),
        canEdit: m.get('canEdit') !== false,
        isManager: !!this.options.isManager,
        title: m.get('title') || '',
        description: m.get('description') || '',
        type: type,
        status: status,
        // Groups the current user may address (from the calendar context). An
        // existing event's group is kept selectable even if the user left it,
        // so a manager editing it does not silently drop the audience.
        groups: this.availableGroups(),
        groupSecId: m.get('groupSecId') || '',
        category: m.get('category') || 'OTHER',
        color: m.get('color') || '',
        colorValue: m.get('color') || CATEGORY_COLORS[m.get('category') || 'OTHER'] || '#2f8f91',
        allDay: !!m.get('allDay'),
        start: dateUtils.formatT(m.get('start')) || '',
        end: dateUtils.formatT(m.get('end')) || '',
        startDate: m.get('start') ? moment(m.get('start')).format('YYYY-MM-DD') : '',
        endDate: m.get('end') ? moment(m.get('end')).format('YYYY-MM-DD') : '',
        halfDayPart: m.get('halfDayPart') || 'FULL',
        recurrenceFreq: m.get('recurrenceFreq') || 'NONE',
        // "Every 2 weeks" is a UI preset over WEEKLY + interval 2.
        recurrencePreset: this.recurrencePreset(m.get('recurrenceFreq'), m.get('recurrenceInterval')),
        recurrenceInterval: m.get('recurrenceInterval') || 1,
        recurrenceByDay: m.get('recurrenceByDay') || '',
        recurrenceUntil: m.get('recurrenceUntil') ?
          moment(m.get('recurrenceUntil')).format('YYYY-MM-DD') : '',
        rejectionReason: m.get('rejectionReason') || '',
        ownerName: m.get('ownerName') || '',
        // approvedBy/approvedAt are set on both approval and rejection.
        approvedByName: m.get('approvedByName') || '',
        approvedAt: m.get('approvedAt') ? moment(m.get('approvedAt')).format('DD/MM/YYYY HH:mm') : '',
        occurrenceDate: m.get('occurrenceDate') || '',
        reminderMinutesBefore: (m.get('reminderMinutesBefore') === 0 || m.get('reminderMinutesBefore')) ?
          String(m.get('reminderMinutesBefore')) : ''
      };
      data.showApproval = data.isManager && type === 'VACATION' &&
        status === 'PENDING_APPROVAL' && !data.isNew;
      data.weekdays = WEEKDAYS;
      return data;
    },

    availableGroups: function () {
      var groups = _.clone(this.options.groups || []);
      var currentSecId = this.model.get('groupSecId');
      if (currentSecId && !_.findWhere(groups, {secId: currentSecId})) {
        groups.push({secId: currentSecId, name: this.model.get('groupName') || currentSecId});
      }
      return groups;
    },

    onShow: function () {
      this.applyDateInputs();
      this.syncVisibility();
      ModalsConfirmationView.prototype.onShow.apply(this, arguments);
    },

    onTypeChange: function () {
      this.syncVisibility();
    },

    onCategoryChange: function () {
      // Snap the colour swatch to the category default so colour follows
      // category unless the user deliberately picks another afterwards.
      this.ui.color.val(CATEGORY_COLORS[this.ui.category.val()] || '#2f8f91');
    },

    onAllDayChange: function () {
      this.applyDateInputs();
      this.syncVisibility();
    },

    /**
     * All-day events use date-only inputs; timed events use datetime-local.
     * Switches the Start/End input types and reformats their current values.
     */
    applyDateInputs: function () {
      var allDay = this.ui.allDay.is(':checked');
      this.toggleDateInput(this.ui.start, allDay);
      this.toggleDateInput(this.ui.end, allDay);
    },

    toggleDateInput: function ($el, allDay) {
      var current = $el.val();
      var ms = current ? moment(current).valueOf() : null;
      $el.attr('type', allDay ? 'date' : 'datetime-local');
      if (ms) {
        $el.val(moment(ms).format(allDay ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm'));
      }
    },

    onRecurrenceChange: function () {
      this.syncVisibility();
    },

    recurrencePreset: function (freq, interval) {
      if (freq === 'WEEKLY' && interval === 2) {
        return 'BIWEEKLY';
      }
      return freq || 'NONE';
    },

    /** The selected repeat option as {freq, interval}; presets expand here. */
    selectedRecurrence: function () {
      var val = this.ui.recurrenceFreq.val();
      if (val === 'BIWEEKLY') {
        return {freq: 'WEEKLY', interval: 2};
      }
      return {freq: val, interval: parseInt(this.ui.recurrenceInterval.val(), 10) || 1};
    },

    /** Show/hide the group picker, vacation half-day and recurrence sub-sections. */
    syncVisibility: function () {
      var type = this.ui.type.val();
      var allDay = this.ui.allDay.is(':checked');
      this.ui.groupRow.toggleClass('hidden', type !== 'GROUP');
      this.ui.halfDayRow.toggleClass('hidden', !(type === 'VACATION' && allDay));

      var val = this.ui.recurrenceFreq.val();
      var rec = this.selectedRecurrence();
      this.ui.recurrenceOptions.toggleClass('hidden', rec.freq === 'NONE');
      // The preset fixes the interval, so the "Every" input is redundant there.
      this.ui.intervalRow.toggleClass('hidden', val === 'BIWEEKLY');
      this.ui.byDayRow.toggleClass('hidden', rec.freq !== 'WEEKLY');
    },

    collectForm: function () {
      var c = this.cloneModel;
      c.set('title', $.trim(this.ui.title.val()));
      c.set('description', this.ui.description.val());
      var type = this.ui.type.val();
      c.set('type', type);
      c.set('groupSecId', type === 'GROUP' ? (this.ui.group.val() || null) : null);
      c.set('category', this.ui.category.val());
      c.set('color', this.ui.color.val() || null);
      var allDay = this.ui.allDay.is(':checked');
      c.set('allDay', allDay);
      var startMs = this.parseDateTime(this.ui.start.val());
      var endMs = this.parseDateTime(this.ui.end.val());
      if (allDay) {
        // Store both bounds at local midnight. `end` is the INCLUSIVE last day;
        // FullCalendar's exclusive end is restored at render time. We avoid any
        // sub-second part (e.g. endOf('day') = .999) because a DATETIME(0)
        // column rounds it up to the next day, which would creep the end
        // forward by a day on every save.
        if (startMs) { startMs = moment(startMs).startOf('day').valueOf(); }
        if (endMs) { endMs = moment(endMs).startOf('day').valueOf(); }
      }
      c.set('start', startMs);
      c.set('end', endMs);
      c.set('halfDayPart', this.ui.halfDayPart.val());

      var rec = this.selectedRecurrence();
      var freq = rec.freq;
      c.set('recurrenceFreq', freq);
      c.set('recurrenceInterval', rec.interval);
      c.set('recurrenceUntil', this.parseDate(this.ui.recurrenceUntil.val()));

      if (freq === 'WEEKLY') {
        var days = [];
        this.ui.byDay.filter(':checked').each(function () {
          days.push($(this).val());
        });
        c.set('recurrenceByDay', days.join(','));
      } else {
        c.set('recurrenceByDay', null);
      }

      var reminderVal = this.ui.reminder.val();
      c.set('reminderMinutesBefore', reminderVal === '' ? null : parseInt(reminderVal, 10));
    },

    parseDateTime: function (value) {
      return value ? moment(value).valueOf() : null;
    },

    parseDate: function (value) {
      return value ? moment(value).valueOf() : null;
    },

    validate: function () {
      if (!this.cloneModel.get('title')) {
        bootbox.alert(_.i18n('calendar.error.titleRequired', null, 'A title is required.'));
        return false;
      }
      if (this.cloneModel.get('type') === 'GROUP' && !this.cloneModel.get('groupSecId')) {
        bootbox.alert(_.i18n('calendar.error.groupRequired', null, 'Choose the group this event is for.'));
        return false;
      }
      var start = this.cloneModel.get('start');
      var end = this.cloneModel.get('end');
      if (!start || !end) {
        bootbox.alert(_.i18n('calendar.error.datesRequired', null, 'Start and end dates are required.'));
        return false;
      }
      if (end < start) {
        bootbox.alert(_.i18n('calendar.error.endBeforeStart', null, 'The end date is before the start date.'));
        return false;
      }
      return true;
    },

    onConfirm: function () {
      if (this.model.get('canEdit') === false) {
        this.windowView.close();
        return;
      }
      this.collectForm();
      if (!this.validate()) {
        return;
      }
      this.load(true, _.i18n('common.saving'));
      this.cloneModel.save()
        .done(_.bind(function () {
          this.load(false);
          if (this.options.onSaved) {
            this.options.onSaved();
          }
          ModalsConfirmationView.prototype.onConfirm.apply(this, arguments);
        }, this))
        .fail(_.bind(function () {
          this.load(false);
          bootbox.alert(_.i18n('calendar.error.saveFailed', null, 'Could not save the event.'));
        }, this));
    },

    onDelete: function () {
      var occurrenceDate = this.model.get('occurrenceDate');
      var isSeries = this.model.get('recurrenceFreq') &&
        this.model.get('recurrenceFreq') !== 'NONE';

      var message = (isSeries && occurrenceDate) ?
        _.i18n('calendar.delete.occurrenceConfirm', null,
          'Delete only this occurrence of the recurring event?') :
        _.i18n('calendar.delete.confirm', null, 'Delete this event?');

      bootbox.confirm(message, _.bind(function (ok) {
        if (!ok) {
          return;
        }
        var deleteArg = (isSeries && occurrenceDate) ? occurrenceDate : null;
        this.model.destroy(deleteArg)
          .done(_.bind(function () {
            if (this.options.onDeleted) {
              this.options.onDeleted();
            }
            this.windowView.close();
          }, this))
          .fail(function () {
            bootbox.alert(_.i18n('calendar.error.deleteFailed', null, 'Could not delete the event.'));
          });
      }, this));
    },

    onApprove: function () {
      App.request('calendar:approve', this.model.get('secId'))
        .done(_.bind(function () {
          if (this.options.onSaved) {
            this.options.onSaved();
          }
          this.windowView.close();
        }, this))
        .fail(function () {
          bootbox.alert(_.i18n('calendar.error.approveFailed', null, 'Could not approve the request.'));
        });
    },

    onReject: function () {
      bootbox.prompt(_.i18n('calendar.reject.reason', null, 'Reason for rejection (optional):'),
        _.bind(function (reason) {
          if (reason === null) {
            return;
          }
          App.request('calendar:reject', this.model.get('secId'), reason)
            .done(_.bind(function () {
              if (this.options.onSaved) {
                this.options.onSaved();
              }
              this.windowView.close();
            }, this))
            .fail(function () {
              bootbox.alert(_.i18n('calendar.error.rejectFailed', null, 'Could not reject the request.'));
            });
        }, this));
    },

    // Static
    open: function (data) {
      require(['windows', 'calendarEditView'], function (Windows, View) {
        var title = data.model.get('secId') ?
          _.i18n('calendar.editEvent', null, 'Edit event') :
          _.i18n('calendar.newEvent', null, 'New event');

        Windows.open({
          view: function () {
            return new View(data);
          },
          title: title,
          modal: true,
          configName: 'calendarEvent'
        });
      });
    }
  });
});

